import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export function buildMlpSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const d_ff = Number(params.d_ff);
  const activation = String(params.activation || 'gelu');
  const variant = String(params.mlp_variant || 'standard');

  if (variant === 'swiglu' || variant === 'geglu' || variant === 'reglu') {
    const actLabel = variant === 'swiglu' ? 'SiLU' : variant === 'geglu' ? 'GELU' : 'ReLU';
    const gate = `${prefix}gate`;
    const up = `${prefix}up`;
    const gateAct = `${prefix}gate_act`;
    const gated = `${prefix}gated`;
    const down = `${prefix}down`;

    g.linear(gate, inputId, [d_model, d_ff]);
    g.linear(up, inputId, [d_model, d_ff]);
    g.activation(gateAct, gate, actLabel);
    g.mul(gated, gateAct, up);
    g.linear(down, gated, [d_ff, d_model]);
    return down;
  }

  if (variant === 'glu') {
    // GLU: gate = W_gate(x), up = W_up(x), out = sigmoid(gate) * up, then W_down(out)
    const gate = `${prefix}gate`;
    const up = `${prefix}up`;
    const gateAct = `${prefix}gate_act`;
    const gated = `${prefix}gated`;
    const down = `${prefix}down`;

    g.linear(gate, inputId, [d_model, d_ff]);
    g.linear(up, inputId, [d_model, d_ff]);
    g.activation(gateAct, gate, 'Sigmoid');
    g.mul(gated, gateAct, up);
    g.linear(down, gated, [d_ff, d_model]);
    return down;
  }

  if (variant === 'squared_relu') {
    // Squared ReLU (Primer): W1(x) -> relu -> square (x * x) -> W2
    const linear1 = `${prefix}linear1`;
    const act = `${prefix}activation`;
    const sq = `${prefix}squared`;
    const linear2 = `${prefix}linear2`;

    g.linear(linear1, inputId, [d_model, d_ff]);
    g.activation(act, linear1, 'ReLU');
    g.mul(sq, act, act);
    g.linear(linear2, sq, [d_ff, d_model]);
    return linear2;
  }

  const linear1 = `${prefix}linear1`;
  const act = `${prefix}activation`;
  const linear2 = `${prefix}linear2`;

  g.linear(linear1, inputId, [d_model, d_ff]);
  g.activation(act, linear1, activation.toUpperCase());
  g.linear(linear2, act, [d_ff, d_model]);

  return linear2;
}

export const mlpComponent: ComponentDef = {
  id: 'mlp',
  name: 'MLP',
  description: 'Transformer feed-forward network with configurable activation and gating variant (Standard, SwiGLU, GeGLU, ReGLU, GLU, Squared ReLU).',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'd_ff', label: 'FFN dimension', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    {
      name: 'mlp_variant',
      label: 'Algorithm variant',
      type: 'select',
      default: 'standard',
      options: [
        { label: 'Standard (W1·act·W2)', value: 'standard' },
        { label: 'SwiGLU (LLaMA/Mistral)', value: 'swiglu' },
        { label: 'GeGLU (PaLM/T5)', value: 'geglu' },
        { label: 'ReGLU (ReLU gate)', value: 'reglu' },
        { label: 'GLU (Gated Linear Unit)', value: 'glu' },
        { label: 'Squared ReLU (Primer)', value: 'squared_relu' }
      ]
    },
    {
      name: 'activation',
      label: 'Activation (standard only)',
      type: 'select',
      default: 'gelu',
      options: [
        { label: 'ReLU', value: 'relu' },
        { label: 'GELU', value: 'gelu' },
        { label: 'SiLU', value: 'silu' }
      ]
    }
  ],
  buildGraph: (params) => {
    const g = new GraphBuilder();
    const seq_len = 128;
    const d_model = Number(params.d_model);
    g.input('x', [seq_len, d_model]);
    const out = buildMlpSubgraph(g, 'x', '', params);
    g.output('out', out);
    return g.build();
  }
};
